// WYSIWYG editor that emits markdown. Same TipTap base as RichTextEditor
// (which emits HTML for event descriptions), but for text stored and
// rendered as markdown — club forum posts. The schema is limited to what
// lib/editorMarkdown serializes.

import { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
} from "lucide-react";
import { editorJsonToMarkdown } from "@/lib/editorMarkdown";
import { markdownToHtml } from "@/lib/markdownToHtml";

interface Props {
  /** Pass "" to clear the editor (e.g. after posting). Other values are not loaded into it. */
  value: string;
  /** Markdown to start with (editing an existing post). Read once, on mount. */
  initialContent?: string;
  onChange: (markdown: string) => void;
  /** Cmd/Ctrl+Enter */
  onSubmit?: () => void;
  placeholder?: string;
  /** Replies: smaller, no heading/quote buttons. */
  compact?: boolean;
  autoFocus?: boolean;
}

export function MarkdownEditor({ value, initialContent, onChange, onSubmit, placeholder, compact, autoFocus }: Props) {
  const editor = useEditor({
    content: initialContent ? markdownToHtml(initialContent) : "",
    extensions: [
      StarterKit.configure({
        heading: compact ? false : { levels: [2] },
        blockquote: compact ? false : undefined,
        codeBlock: false,
        horizontalRule: false,
        underline: false, // no markdown equivalent
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: "https",
          HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
        },
      }),
    ],
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: `markdown-editor focus:outline-none ${compact ? "min-h-[3rem]" : "min-h-[6rem]"} text-sm leading-relaxed`,
      },
      handleKeyDown: (_view, event) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && onSubmit) {
          event.preventDefault();
          onSubmit();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? "" : editorJsonToMarkdown(editor.getJSON())),
  });

  // Clear when the parent resets the value (after a successful post).
  useEffect(() => {
    if (editor && value === "" && !editor.isEmpty) editor.commands.clearContent();
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div>
      <div className="relative">
        <EditorContent editor={editor} />
        {editor.isEmpty && placeholder && (
          <div className="pointer-events-none absolute top-0 left-0 text-sm text-neutral-400/80 dark:text-neutral-500/80">
            {placeholder}
          </div>
        )}
      </div>
      <Toolbar editor={editor} compact={compact} />
    </div>
  );
}

type EditorInstance = NonNullable<ReturnType<typeof useEditor>>;

function Toolbar({ editor, compact }: { editor: EditorInstance; compact?: boolean }) {
  const [, force] = useState(0);
  // Editor state isn't React state; re-render on changes so active buttons update.
  useEffect(() => {
    const tick = () => force((n) => n + 1);
    editor.on("transaction", tick);
    return () => {
      editor.off("transaction", tick);
    };
  }, [editor]);

  const onLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const chain = () => editor.chain().focus();
  const buttons: { label: string; icon: typeof Bold; active: boolean; run: () => void; hidden?: boolean }[] = [
    { label: "Bold", icon: Bold, active: editor.isActive("bold"), run: () => chain().toggleBold().run() },
    { label: "Italic", icon: Italic, active: editor.isActive("italic"), run: () => chain().toggleItalic().run() },
    { label: "Strikethrough", icon: Strikethrough, active: editor.isActive("strike"), run: () => chain().toggleStrike().run() },
    { label: "Heading", icon: Heading2, active: editor.isActive("heading"), run: () => chain().toggleHeading({ level: 2 }).run(), hidden: compact },
    { label: "Bullet list", icon: List, active: editor.isActive("bulletList"), run: () => chain().toggleBulletList().run() },
    { label: "Numbered list", icon: ListOrdered, active: editor.isActive("orderedList"), run: () => chain().toggleOrderedList().run() },
    { label: "Quote", icon: Quote, active: editor.isActive("blockquote"), run: () => chain().toggleBlockquote().run(), hidden: compact },
    { label: "Link", icon: LinkIcon, active: editor.isActive("link"), run: onLink },
  ];

  return (
    <div className="flex items-center gap-0.5 mt-2 pt-2 border-t border-black/5 dark:border-white/10 overflow-x-auto">
      {buttons
        .filter((b) => !b.hidden)
        .map(({ label, icon: Icon, active, run }) => (
          <button
            key={label}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={active}
            onMouseDown={(e) => e.preventDefault()} // keep the editor selection
            onClick={run}
            className={`w-8 h-8 flex-shrink-0 inline-flex items-center justify-center rounded-md transition-colors ${
              active
                ? "bg-primary-500/15 text-primary-500"
                : "text-neutral-500 dark:text-neutral-400 hover:bg-base-200 hover:text-neutral-700 dark:hover:text-neutral-200"
            }`}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
    </div>
  );
}
